/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 4 2023
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef, useState, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { _T } from "@rfice/language";
import { Column, Icon, Loader, Row, Select, Switch, Typography } from "@/frameworks/designStuff/components";
import Maybe from "@/frameworks/libraries/components/Maybe";
import { colors } from "@/frameworks/core/styles";
import { useDeviceManager, DeviceManager } from "@rsupport/devicemanager";
import { Properties, useProperties } from "@rsupport/libraries";

import * as S from "../../emotion";
import Layout from "../../Layout";
import MicVisualizer from "./MicVisualizer";
import SpeakerVisualizer from "./SpeakerVisualizer";
import styled from "@emotion/styled";

const LOGTAG = "#Device#";

export default function Device() {
	const devicelist = useDeviceManager((state) => state.devices);
	const devicemanager = useDeviceManager(
		useShallow((state) => ({
			camera: state.camera,
			microphone: state.microphone,
			speaker: state.speaker
		}))
	);
	const camera_list = devicelist.filter((device) => device.kind === "videoinput");
	const microphone_list = devicelist.filter((device) => device.kind === "audioinput");
	const speaker_list = devicelist.filter((device) => device.kind === "audiooutput");

	const device_options = useProperties(
		(state) =>
			state.properties[state.selected].device_options as {
				video_enable: boolean;
				audio_enable: boolean;
				switch_new_device: boolean;
				use_noise_cancellation: boolean;
			}
	);

	const videoRef = useRef<HTMLVideoElement>(null);
	const media_stream_ref = useRef<MediaStream | null>(null);
	const [video_playing, set_video_playing] = useState<boolean>(false);

	useEffect(() => {
		console.debug("devicelist:", devicelist);
	}, [devicelist]);

	const media_play = useCallback(async () => {
		media_close();
		const mediaConstraints = DeviceManager.instance.get_media_constraints({ video: {}, audio: {} });
		if (Object.keys(mediaConstraints).length > 0) {
			const mediaStream = await navigator.mediaDevices.getUserMedia(mediaConstraints).catch((error) => {
				console.warn(LOGTAG, "Media play failed", error, mediaConstraints);
				return null;
			});

			if (mediaStream) {
				if (mediaStream.getVideoTracks()[0]) {
					mediaStream.getVideoTracks()[0].enabled = device_options.video_enable;
				}
				if (mediaStream.getAudioTracks()[0]) {
					mediaStream.getAudioTracks()[0].enabled = device_options.audio_enable;
				}
				media_stream_ref.current = mediaStream;

				if (media_stream_ref.current && videoRef.current) {
					videoRef.current.onloadedmetadata = () => {
						set_video_playing(true);
					};
					videoRef.current.srcObject = media_stream_ref.current;
					console.debug(LOGTAG, "Preview media playing", mediaConstraints);
				}
			}
		}
	}, [device_options]);

	const media_close = () => {
		if (media_stream_ref.current) {
			console.debug(LOGTAG, "Preview media close", media_stream_ref.current);
			media_stream_ref.current.getTracks().forEach((track) => track.stop());
		}
	};

	useEffect(() => {
		return () => {
			media_close();
		};
	}, []);

	useEffect(() => {
		media_play().then(() => {});
	}, [devicemanager.camera, devicemanager.microphone, device_options]);

	const toggleCam = useCallback(() => {
		// 비디오 트랙 가져오기
		if (media_stream_ref.current) {
			const videoTrack = media_stream_ref.current.getVideoTracks()[0];

			// 비디오 트랙 활성화 또는 비활성화
			if (videoTrack) {
				videoTrack.enabled = !device_options.video_enable;
			}
		}
		Properties.instance.set("device_options", {
			video_enable: !device_options.video_enable
		});
	}, [device_options.video_enable]);

	const toggleMic = useCallback(() => {
		// 오디오 트랙 가져오기
		if (media_stream_ref.current) {
			const audioTrack = media_stream_ref.current.getAudioTracks()[0];

			// 오디오 트랙 활성화 또는 비활성화
			if (audioTrack) {
				audioTrack.enabled = !device_options.audio_enable;
			}
		}
		Properties.instance.set("device_options", {
			audio_enable: !device_options.audio_enable
		});
	}, [device_options.audio_enable]);

	const handleChange = (e: React.ChangeEvent<HTMLElement>, type: "camera" | "mic" | "speaker") => {
		const target = e.target as HTMLSelectElement;
		const { value } = target;

		if (type === "camera") {
			DeviceManager.instance.set_current_device("videoinput", value);
		} else if (type === "mic") {
			DeviceManager.instance.set_current_device("audioinput", value);
		} else if (type === "speaker") {
			DeviceManager.instance.set_current_device("audiooutput", value);
		}
	};

	return (
		<Layout title="장치 설정">
			<S.VideoContent gap={10} horizonAlign="center">
				<S.VideoWrapper>
					<S.Video ref={videoRef} autoPlay playsInline muted />
					<Maybe test={!video_playing}>
						<Loader />
					</Maybe>
					<S.DefaultVideoWrapper>
						{camera_list.length === 0 ? (
							<S.DefaultVideo status="no-cam">
								<Icon name="DefaultCamOff24" size={200} fill={colors.neutral_20_100} />
							</S.DefaultVideo>
						) : !device_options.video_enable ? (
							<S.DefaultVideo status="cam-off">
								<Icon name="DefaultCamOff24" size={200} fill={colors.neutral_20_100} />
							</S.DefaultVideo>
						) : null}
					</S.DefaultVideoWrapper>
				</S.VideoWrapper>

				{/* TODO 장치 상태관련 - 커뮤니케이션 바 관련하여 내용 추가되어야함 */}
				<Column gap={8} verticalAlign="center" css={{ width: "100%" }}>
					<S.AlarmSection>
						<S.LabelText>{_T("카메라")}</S.LabelText>
						<Switch
							checked={device_options.video_enable && camera_list.length !== 0}
							onChange={toggleCam}
						/>
					</S.AlarmSection>
					<Select
						style={{ width: "100%" }}
						value={devicemanager.camera.id}
						onChange={(e) => handleChange(e, "camera")}
						rightIcon="down"
						disabled={camera_list.length === 0}
						options={camera_list.map((camera) => ({
							label: camera.name,
							value: camera.id,
							meta: camera.id
						}))}
					/>
				</Column>
				<Column gap={8} verticalAlign="center" css={{ width: "100%" }}>
					<S.AlarmSection>
						<S.LabelText>{_T("마이크")}</S.LabelText>
						<Switch
							checked={device_options.audio_enable && microphone_list.length !== 0}
							onChange={toggleMic}
						/>
					</S.AlarmSection>
					<Select
						style={{ width: "100%" }}
						value={microphone_list.length === 0 ? _T("장치 없음") : devicemanager.microphone.id}
						onChange={(e) => handleChange(e, "mic")}
						rightIcon="down"
						disabled={microphone_list.length === 0}
						options={microphone_list.map((mic) => ({
							label: mic.name,
							value: mic.id,
							meta: mic.id
						}))}
					/>
					<MicVisualizer
						stream={media_stream_ref.current}
						list={microphone_list}
						isDisabled={microphone_list.length === 0 || !device_options.audio_enable}
					/>
				</Column>

				<Column gap={8} verticalAlign="center" css={{ width: "100%" }}>
					<Select
						label={_T("스피커")}
						style={{ width: "100%" }}
						value={speaker_list.length === 0 ? _T("장치 없음") : devicemanager.speaker.id}
						onChange={(e) => handleChange(e, "speaker")}
						rightIcon="down"
						disabled={speaker_list.length === 0}
						options={speaker_list.map((speaker) => ({
							label: speaker.name,
							value: speaker.id,
							meta: speaker.id
						}))}
					/>
					<SpeakerVisualizer id={devicemanager.speaker.id} isDisabled={speaker_list.length === 0} />
				</Column>
				<Column verticalAlign="center" css={{ width: "100%" }}>
					<Section>
						<Typography.p typoSize={"Body4"}>{_T("새로운 장치 연결 시 바로 사용")}</Typography.p>
						<Switch
							checked={device_options.switch_new_device}
							onChange={() => {
								Properties.instance.set("device_options", {
									switch_new_device: !device_options.switch_new_device
								});

								DeviceManager.instance.set_options({
									switch_new_device: !device_options.switch_new_device
								});
							}}
						/>
					</Section>
					<Section>
						<Typography.p typoSize={"Body4"}>{_T("주변소음 제거 기능 사용")}</Typography.p>
						<Switch
							checked={device_options.use_noise_cancellation}
							onChange={() => {
								Properties.instance.set("device_options", {
									use_noise_cancellation: !device_options.use_noise_cancellation
								});
							}}
						/>
					</Section>
				</Column>
			</S.VideoContent>
		</Layout>
	);
}

const Section = styled(Row)`
	padding: 8px 0;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;
